import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { differenceInDays } from "date-fns";

import { Chip, Divider, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import NoCommentView from "components/Screens/Timeoff/ManageTimeoff/PendingLeaves/PendingLeavesCard/NoCommentView";
import LeaveType from "components/Screens/Timeoff/ManageTimeoff/Common/LeaveType";
import EditByHrStatus from "components/Screens/Timeoff/ManageTimeoff/Common/EditByHrStatus";
import MedicalDocsModal from "components/Screens/Timeoff/ManageTimeoff/MedicalDocsModal";
import { convertDate, getWorkingDuration } from "components/Screens/CommonUtils";
import DocumentsList from "components/Screens/Timeoff/ManageTimeoff/PendingLeaves/PendingLeavesCard/DocumentsList";
import EmployeeAvatar from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/EmployeeAvatar";

import { hasPermission } from "hooks/usePermission";

import { baseUrl, s3ImagesBaseURL } from "util/APIBaseUrl";
import { makeRequest } from "util/APIHelper";
import { formatLeaves } from "helpers";

import { showSnackbar } from "store/actions/Utility";
import { GET_LEAVE_REQUESTS_SUCCESS } from "store/constants/Timeoff";
import { FetchLeaveRequests } from "store/actions/Timeoff/LeaveRequests";

import PendingTimeIcon from "assets/images/pending-timer.svg";
import LeftArrow from "assets/images/left-arrow.svg";
import CrossIcon from "assets/svgs/CrossIcon";
import MedicalDocsIcon from "assets/images/medical-docs.svg";
import { AccessTime as AccessTimeIcon } from "@mui/icons-material";

import "./PendingLeavesCard.scss";

function PendingLeavesCard({
  leave: {
    leaveType,
    documentsAttached,
    employeeId,
    employeeName,
    country,
    dateFrom,
    dateTo,
    createdAt,
    description,
    noOfLeaves,
    requestId,
    documents,
    pictureVersion,
    hrEdit,
    hrApproved,
    status,
    swapDateFrom,
    swapDateTo,
    swapDescription,
  },
}) {
  const dispatch = useDispatch();
  const { anniversaryBasedTimeOff, swappedLeavesRevamp } = useFlags();
  const leaveRequests = useSelector((state) => state?.TimeoffLeaveRequests || []);

  const [imageSrc, setImageSrc] = useState(`${s3ImagesBaseURL}/placeholder-avatar.svg`);

  const [medicalDocsModal, setMedicalDocsModal] = useState({
    show: false,
    id: null,
  });
  const [isCancellingRequest, setIsCancellingRequest] = useState({ state: false, showSnackbar: false });

  const handleCancelLeave = async () => {
    // A card rendered without a request behind it has nothing to cancel. Sending the
    // request anyway puts the literal string "undefined" in the URL, which the API
    // rejects with a 500.
    if (!requestId) return;

    setIsCancellingRequest({
      state: true,
      showSnackbar: false,
    });

    const path = anniversaryBasedTimeOff
      ? `${baseUrl}/v2/employee/${requestId}/leave/Cancelled`
      : `${baseUrl}/employee/${requestId}/leave/Cancelled`;

    const response = await makeRequest({ path, method: "PUT" }, false);
    const cancelled = Boolean(response?.ok);

    setIsCancellingRequest({
      state: false,
      showSnackbar: true,
      success: cancelled,
    });

    // Leave the list untouched on failure so the request the user still has stays visible.
    if (!cancelled) return;

    const pendingLeaveReqs = leaveRequests?.leaveRequests?.pending?.filter((req) => req.requestId !== requestId);
    const leaveReqs = {
      ...leaveRequests.leaveRequests,
      pending: pendingLeaveReqs,
    };

    dispatch({
      type: GET_LEAVE_REQUESTS_SUCCESS,
      payload: { payload: { data: leaveReqs } },
    });

    dispatch(FetchLeaveRequests({ anniversaryBasedTimeOff }));
  };

  useEffect(() => {
    if (!isCancellingRequest.showSnackbar) return;
    dispatch(
      isCancellingRequest.success
        ? showSnackbar({ dispatch, type: "success", message: "Leave request has been cancelled succesfully" })
        : showSnackbar({ dispatch, type: "error", message: "Could not cancel this leave request. Please try again." })
    );
  }, [isCancellingRequest]);

  useEffect(() => {
    if (employeeId && pictureVersion) {
      const empImageUrl = `${baseUrl}/employee/${employeeId}/logo?id=${pictureVersion}&variant=thumbnail`;
      setImageSrc(empImageUrl);
    } else {
      setImageSrc(`${s3ImagesBaseURL}/placeholder-avatar.svg`);
    }
  }, [employeeId, pictureVersion]);

  const canCancel = hasPermission("timeoff.cancel");

  const formattedLeaveCount = formatLeaves(noOfLeaves);

  return (
    <div className="pending-leaves-card-container">
      <MedicalDocsModal
        open={medicalDocsModal.show}
        title="Medical Documents"
        leaveRequestId={requestId}
        closeModal={() =>
          setMedicalDocsModal({
            show: false,
            id: null,
          })
        }
        {...{ leaveType, dateFrom, dateTo, noOfLeaves }}
      />
      <div className="pending-leaves-top-section">
        <div className="pending-leaves-date">
          {anniversaryBasedTimeOff ? (
            <div className="ml-1 mr-05 mt-025">
              <AccessTimeIcon className="pending-leaves-time-icon-revamped" />
            </div>
          ) : (
            <img className="pending-leaves-time-icon" src={PendingTimeIcon} alt="pending time" />
          )}
          <div>
            <div className="pending-leave-reason" id="leave-type">
              {leaveType}
            </div>

            <div className="pending-leave-duration">
              <div className="pending-leave-date" id="start-date">
                {convertDate(dateFrom)}
              </div>
              {dateFrom !== dateTo && (
                <>
                  <img className="pending-leave-arrow" src={LeftArrow} alt="left arrow" />
                  <div className="pending-leave-date" id="end-date">
                    {convertDate(dateTo)}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {canCancel && requestId && (
          <Chip
            variant="outlined"
            className="pending-leave-chip"
            icon={<CrossIcon className="chip-leaves-pending-icon" fill="#52537A" />}
            clickable
            label={
              <div className="cancel_leave_request_chip_label" id="cancel-request-btn" onClick={handleCancelLeave}>
                Cancel Request
              </div>
            }
          />
        )}
      </div>

      <Divider light />
      <div className=" pending-timeoff-details-wrapper" style={{ marginTop: "9px" }}>
        <div className="pending-timeoff-details">
          <div className="pending-timeoff-text-emp">Duration</div>
          <Chip
            variant="outlined"
            className="custom-chip"
            id="total-timeoff-used"
            label={`${formattedLeaveCount}  ${noOfLeaves == 1 ? "leave" : "leaves"}`}
          />
        </div>

        {leaveType === "Swapped" && swapDateFrom && swapDateTo && swappedLeavesRevamp && (
          <div className="pending-timeoff-details">
            <div className="pending-timeoff-text-emp">Swap with</div>
            <Chip
              variant="outlined"
              className="custom-chip"
              id="swap-dates"
              label={
                <div className="flex items-center">
                  <span className="para-body-s-regular">
                    {swapDateFrom === swapDateTo
                      ? convertDate(swapDateFrom)
                      : `${convertDate(swapDateFrom)} - ${convertDate(swapDateTo)}`}
                  </span>
                  {swapDescription && (
                    <Tooltip title={swapDescription} placement="top">
                      <InfoOutlinedIcon className="swap-info-icon ml-05" />
                    </Tooltip>
                  )}
                </div>
              }
            />
          </div>
        )}

        <div className="pending-timeoff-details">
          <div className="pending-timeoff-text-emp">Notice Period</div>
          <Chip
            variant="outlined"
            className="custom-chip"
            id="notice-period"
            label={`${differenceInDays(new Date(dateFrom), new Date(createdAt))} Days`}
          />
        </div>

        {anniversaryBasedTimeOff && <DocumentsList documents={documents} />}

        <div className="pending-timeoff-details">
          <div className="pending-timeoff-text-emp">Status</div>

          <LeaveType type="pending" />
        </div>
        {!anniversaryBasedTimeOff && country === "Peru" && (leaveType === "Sick" || hrEdit || hrApproved) && (
          <div className="pending-timeoff-details">
            <div className="pending-timeoff-text-emp">HR doc review</div>
            <EditByHrStatus
              style={{ width: "fit-content !important" }}
              hrEdit={hrEdit}
              hrApproved={hrApproved}
              leaveStatus={status}
            />
          </div>
        )}
      </div>

      <Divider light />

      <div className="pending-timeoff-comments">Comments:</div>
      <div className="pending-timeoff-comments-container ml-1 mr-05">
        <EmployeeAvatar
          img={imageSrc}
          size={{
            width: "44px",
            height: "44px",
            borderRadius: "50%",
          }}
        />

        {description ? (
          <div className="pending-timeoff-comment-wrapper">
            <div className="pending-timeoff-comment-description break-word" id="leave-description">
              {description || ""}
            </div>
            <div className="pending-timeoff-comment-name" id="emp-name">
              {employeeName}
            </div>
          </div>
        ) : (
          <NoCommentView />
        )}

        {!anniversaryBasedTimeOff && leaveType === "Sick" && documentsAttached && (
          <div
            className="flex medical-docs-btn cursor-pointer"
            onClick={() =>
              setMedicalDocsModal({
                show: true,
                id: requestId,
              })
            }
          >
            <img src={MedicalDocsIcon} alt="medical docs" />
            <div className="flex flex-col" style={{ alignSelf: "center", marginLeft: "0.5rem" }}>
              <div className="pop-regular title-md medical-docs-btn-label">Medical Docs</div>
              <div className="label-sm pop-regular neutral-60" id="medical-docs-btn">
                View
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="pending-timeoff-request-date ml-05" id="request-date">
        Request date: {convertDate(createdAt)} ({getWorkingDuration(createdAt)} ago)
      </div>
    </div>
  );
}

export default PendingLeavesCard;
